import { getSupabaseClient } from '../utils/supabaseClient';
import { getOrCreateDeviceId, getDeviceLabel } from '../utils/deviceId';

/**
 * The client half of the two-active-sessions-per-user limit.
 *
 * Backend: supabase/migrations/20260913_user_sessions_device_limit.sql — register_device_session()
 * claims this device and evicts the oldest beyond the cap; is_current_device_active() answers
 * whether this device still holds one. Both are SECURITY DEFINER on auth.uid(), so this file
 * never names a user and cannot ask about anyone else.
 *
 * WHY A FOUR-STATE OUTCOME AND NOT A BOOLEAN
 * Taken from ProofioV2's device_session_service.dart, whose most important line is a warning
 * about its own predecessor: the Kotlin app collapsed "the server refused" and "we could not
 * reach the server", which "would lock a legitimate user out of their own account for having
 * no signal at the moment they signed in."
 *
 * That risk is HIGHER here, not lower. This app is used in basements, crawlspaces, plant rooms
 * and rural driveways. A tech losing signal must never be signed out of the job they are
 * standing in front of. So:
 *
 *   ACTIVE      the server confirmed this device holds a session
 *   REPLACED    the server confirmed it does NOT — the only outcome that signs anyone out
 *   REFUSED     the server answered and said no (an actual error, not a network failure)
 *   UNREACHABLE we could not ask. ALWAYS permissive. Never signs anyone out.
 *
 * Only a verdict the server actually gave is allowed to be destructive.
 */

export const DEVICE_SESSION_OUTCOME = {
  ACTIVE: 'active',
  REPLACED: 'replaced',
  REFUSED: 'refused',
  UNREACHABLE: 'unreachable',
};

// Same cap as c_max_devices in register_device_session (live DB): the number of devices that
// can hold a session at once.
const MAX_DEVICES = 2;

// One message per reason this device's session row stops counting as active. The server only
// answers yes/no (is_current_device_active), so describeEndedSession reads the user's own rows
// to tell the reasons apart.
export const REPLACED_DEVICE_MESSAGE =
  `Your account was signed in on another device. Only ${MAX_DEVICES} devices can be signed in at once, so this one was signed out. Sign in again to continue here.`;
export const SIGNED_OUT_EVERYWHERE_MESSAGE =
  'You were signed out of all devices from another device. Sign in again to continue.';
export const TIMED_OUT_MESSAGE =
  'Your session timed out after a period of inactivity. Sign in again to continue.';
export const SESSION_ENDED_MESSAGE =
  'This session has ended. Sign in again to continue.';

/**
 * Did the SERVER answer, or did we fail to reach it?
 *
 * A PostgREST error carries a `code` (Postgres SQLSTATE) or an HTTP `status`. A dropped
 * connection surfaces as a bare TypeError ("Failed to fetch") with neither. Getting this
 * backwards is the exact bug the header warns about, so it is a named function rather than an
 * inline condition.
 */
const serverAnswered = (error) =>
  Boolean(error && (error.code || error.status || error.statusCode));

export class DeviceSessionService {
  // Last verdict and in-flight request of verifyCurrentDevice, per tab (see maxAgeMs).
  static _lastVerdict = null;
  static _verifyInFlight = null;

  /**
   * Claim this device for the signed-in user. Call after EVERY successful sign-in.
   *
   * Returns ACTIVE whether or not it displaced someone — this device holds a session either
   * way, which is all the caller needs to know.
   */
  static async registerCurrentDevice() {
    const deviceId = getOrCreateDeviceId();
    if (!deviceId) return { outcome: DEVICE_SESSION_OUTCOME.UNREACHABLE };

    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.rpc('register_device_session', {
        p_device_id: deviceId,
        p_device_name: getDeviceLabel(),
        p_app_version: null,
      });

      if (!error) return { outcome: DEVICE_SESSION_OUTCOME.ACTIVE };

      console.error('device session: register failed', error);
      return {
        outcome: serverAnswered(error)
          ? DEVICE_SESSION_OUTCOME.REFUSED
          : DEVICE_SESSION_OUTCOME.UNREACHABLE,
      };
    } catch (e) {
      // A thrown error here is a transport failure, never a verdict.
      console.warn('device session: register unreachable', e?.message);
      return { outcome: DEVICE_SESSION_OUTCOME.UNREACHABLE };
    }
  }

  /**
   * Has the SERVER deactivated this device's row (is_active = false)? That happens when a newer
   * sign-in displaced it (register_device_session), or on Logout / "Sign out of all devices"
   * (endDeviceSessions). Expiry by inactivity leaves is_active = true and is NOT reported here.
   *
   * True only when the row was read and says so. A missing row (never registered), a failed
   * read or no device id answers false, so the caller behaves as it did before — permissive,
   * per the header.
   */
  static async wasDeviceDeactivated(authUserId) {
    const deviceId = getOrCreateDeviceId();
    if (!deviceId || !authUserId) return false;
    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase
        .from('user_sessions')
        .select('is_active')
        .eq('user_id', authUserId)
        .eq('device_fingerprint', deviceId)
        .maybeSingle();
      if (error || !data) return false;
      return data.is_active === false;
    } catch (e) {
      return false;
    }
  }

  /**
   * Record activity on this device: push expires_at forward so the session timeout counts
   * from the last activity, not from sign-in.
   *
   * The row is per DEVICE (device id lives in localStorage), so every tab of this browser
   * shares it — activity in any tab keeps all of them alive, and every tab's
   * verifyCurrentDevice reads the same answer.
   *
   * The timeout length is NOT re-derived here: register_device_session owns it and wrote
   * expires_at - last_activity = the company's timeout, so that window is reused as-is.
   * Only a session the SERVER still calls active (is_current_device_active, server clock) is
   * touched, so this does not revive a session already replaced or timed out. Times only
   * move forward, so a browser clock running slow can never shorten a session. Failures
   * are ignored: the worst case is the timeout counting from the previous touch.
   */
  static async touchCurrentDevice() {
    const deviceId = getOrCreateDeviceId();
    if (!deviceId) return;

    try {
      // Reuse the minute-interval check's verdict (UserContext) instead of asking the server a
      // second time each minute. Safe to be up to 60s old: the update below is itself guarded
      // by is_active = true and expires_at > now, so it cannot revive a replaced or expired row.
      const { outcome } = await DeviceSessionService.verifyCurrentDevice({ maxAgeMs: 60000 });
      if (outcome !== DEVICE_SESSION_OUTCOME.ACTIVE) return;

      const supabase = getSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();
      const authUserId = session?.user?.id;
      if (!authUserId) return;

      const { data: row, error } = await supabase
        .from('user_sessions')
        .select('id, last_activity, expires_at')
        .eq('user_id', authUserId)
        .eq('device_fingerprint', deviceId)
        .eq('is_active', true)
        .maybeSingle();
      if (error || !row) return;

      const lastActivity = new Date(row.last_activity).getTime();
      const expiresAt = new Date(row.expires_at).getTime();
      const windowMs = expiresAt - lastActivity;
      if (!(windowMs > 0)) return;

      const now = Date.now();
      const nextActivity = Math.max(now, lastActivity);
      const nextExpires = Math.max(now + windowMs, expiresAt);
      if (nextExpires === expiresAt) return;

      await supabase
        .from('user_sessions')
        .update({
          last_activity: new Date(nextActivity).toISOString(),
          expires_at: new Date(nextExpires).toISOString(),
        })
        .eq('id', row.id)
        .eq('is_active', true)
        .gt('expires_at', new Date(now).toISOString());
    } catch (e) {
      console.warn('device session: touch unreachable', e?.message);
    }
  }

  /**
   * Release this user's device slot(s) on sign-out. Call BEFORE supabase.auth.signOut — the
   * update needs the session (RLS company_user_sessions_update).
   *
   * allDevices = false: just this browser (Logout). Frees the slot under the two-device cap
   * instead of holding it until it times out.
   * allDevices = true: every device of this user (Settings → Security, "Sign out of all
   * devices"). This is what makes the OTHER devices leave promptly: a global signOut revokes
   * their refresh tokens, but their current access token stays valid until it expires (up to
   * an hour). Their is_current_device_active check (every 60s and on focus, UserContext)
   * now answers false, so they take the existing sign-out path.
   *
   * Best effort, never throws, capped so a dead network cannot hold up the sign-out itself.
   * register_device_session re-activates the row on the next sign-in (ON CONFLICT).
   */
  static async endDeviceSessions({ allDevices = false } = {}) {
    const work = (async () => {
      try {
        const supabase = getSupabaseClient();
        const { data: { session } } = await supabase.auth.getSession();
        const authUserId = session?.user?.id;
        if (!authUserId) return;

        let query = supabase
          .from('user_sessions')
          .update({ is_active: false, last_activity: new Date().toISOString() })
          .eq('user_id', authUserId)
          .eq('is_active', true);
        if (!allDevices) {
          const deviceId = getOrCreateDeviceId();
          if (!deviceId) return;
          query = query.eq('device_fingerprint', deviceId);
        }
        const { error } = await query;
        if (error) console.warn('device session: end failed', error?.message);
      } catch (e) {
        console.warn('device session: end unreachable', e?.message);
      }
    })();
    await Promise.race([work, new Promise((resolve) => setTimeout(resolve, 4000))]);
  }

  /**
   * Why does this device no longer hold a session? Call only after verifyCurrentDevice said
   * REPLACED. Reads the signed-in user's own user_sessions rows (the access token is still
   * valid at this point) and returns the message for the cause:
   *
   *   this device's row still active      -> it passed expires_at: timed out
   *   MAX_DEVICES other devices active    -> evicted by a newer sign-in (register_device_session)
   *   no device of this user active       -> "Sign out of all devices" (endDeviceSessions)
   *   anything else / the read fails      -> a neutral "session has ended"
   *
   * Never throws, and capped at 4s so a stalled read cannot hold up the sign-out.
   */
  static async describeEndedSession() {
    return Promise.race([
      DeviceSessionService._readEndedCause(),
      new Promise((resolve) => setTimeout(() => resolve(SESSION_ENDED_MESSAGE), 4000)),
    ]);
  }

  static async _readEndedCause() {
    const deviceId = getOrCreateDeviceId();
    try {
      const supabase = getSupabaseClient();
      const { data: { session } } = await supabase.auth.getSession();
      const authUserId = session?.user?.id;
      if (!authUserId || !deviceId) return SESSION_ENDED_MESSAGE;

      const { data: rows, error } = await supabase
        .from('user_sessions')
        .select('device_fingerprint, is_active, expires_at')
        .eq('user_id', authUserId);
      if (error || !Array.isArray(rows)) return SESSION_ENDED_MESSAGE;

      const mine = rows.find((r) => r.device_fingerprint === deviceId);
      if (mine?.is_active) return TIMED_OUT_MESSAGE;

      const now = Date.now();
      const othersActive = rows.filter((r) =>
        r.device_fingerprint
        && r.device_fingerprint !== deviceId
        && r.is_active
        && new Date(r.expires_at).getTime() > now
      ).length;
      if (othersActive >= MAX_DEVICES) return REPLACED_DEVICE_MESSAGE;
      if (mine && othersActive === 0) return SIGNED_OUT_EVERYWHERE_MESSAGE;
      return SESSION_ENDED_MESSAGE;
    } catch (e) {
      return SESSION_ENDED_MESSAGE;
    }
  }

  /**
   * Does this device still hold a session? REPLACED is the only outcome a caller may act on
   * destructively.
   *
   * maxAgeMs > 0 lets a caller reuse a verdict this tab already got (or is getting) within
   * that many ms. The default (0) always asks the server.
   */
  static async verifyCurrentDevice({ maxAgeMs = 0 } = {}) {
    if (maxAgeMs > 0) {
      if (DeviceSessionService._verifyInFlight) return DeviceSessionService._verifyInFlight;
      const last = DeviceSessionService._lastVerdict;
      if (last && Date.now() - last.at < maxAgeMs) return last.result;
    }
    const p = DeviceSessionService._verifyNow().then((result) => {
      DeviceSessionService._lastVerdict = { at: Date.now(), result };
      return result;
    }).finally(() => {
      if (DeviceSessionService._verifyInFlight === p) DeviceSessionService._verifyInFlight = null;
    });
    DeviceSessionService._verifyInFlight = p;
    return p;
  }

  static async _verifyNow() {
    const deviceId = getOrCreateDeviceId();
    if (!deviceId) return { outcome: DEVICE_SESSION_OUTCOME.UNREACHABLE };

    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase.rpc('is_current_device_active', {
        p_device_id: deviceId,
      });

      if (error) {
        console.warn('device session: verify failed', error?.message);
        return {
          outcome: serverAnswered(error)
            ? DEVICE_SESSION_OUTCOME.REFUSED
            : DEVICE_SESSION_OUTCOME.UNREACHABLE,
        };
      }

      return data === true
        ? { outcome: DEVICE_SESSION_OUTCOME.ACTIVE }
        : { outcome: DEVICE_SESSION_OUTCOME.REPLACED, message: REPLACED_DEVICE_MESSAGE };
    } catch (e) {
      console.warn('device session: verify unreachable', e?.message);
      return { outcome: DEVICE_SESSION_OUTCOME.UNREACHABLE };
    }
  }
}

export default DeviceSessionService;
